import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, BookOpen, Tag } from 'lucide-react';
import oneSpaceApi from '../lib/onespace-api';

interface KnowledgeArticle {
  slug: string;
  title: string;
  content: string;
  author: string;
  created_at: string;
  updated_at: string;
  tags: string[];
}

const KnowledgeArticle: React.FC = () => {
  // Haal de slug parameter uit de URL
  const { slug } = useParams<{ slug: string }>();
  
  // State voor het artikel
  const [article, setArticle] = useState<KnowledgeArticle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch het artikel wanneer de component laadt
  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await oneSpaceApi.get<KnowledgeArticle>(`/api/knowledge/${slug}`);
        setArticle(response.data);
      } catch (err) {
        console.error('Error fetching knowledge article:', err);
        setError('Het artikel kon niet worden geladen. Probeer het later opnieuw.');
        
        // Voor demo/ontwikkeling: genereer een dummy artikel
        if (process.env.NODE_ENV === 'development') {
          setArticle({
            slug: slug,
            title: `Knowledgebase Artikel: ${slug}`,
            content: `
              <p>Dit is een dummy knowledgebase artikel voor slug "${slug}". In een echte implementatie zou dit worden opgehaald uit de database.</p>
              <h2>Voorbeeld inhoud</h2>
              <p>Dit is een voorbeeld van rijke inhoud die in een kennisbank artikel kan voorkomen. Het kan opmaak, kopjes, lijsten en meer bevatten.</p>
              <ul>
                <li>Punt 1: Belangrijke informatie</li>
                <li>Punt 2: Meer details</li>
                <li>Punt 3: Aanvullende context</li>
              </ul>
              <p>Dit artikel kan worden gebruikt voor het testen van de layout en styling van kennisbank artikelen.</p>
            `,
            author: 'System',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            tags: ['dummy', 'knowledge', 'test']
          });
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchArticle();
  }, [slug]);
  
  // Helper functie om een datum te formatteren
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <Button
        variant="outline"
        size="sm"
        className="mb-4 flex items-center gap-2"
        onClick={() => window.history.back()}
      >
        <ArrowLeft className="h-4 w-4" />
        Terug
      </Button>
      
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <Card className="w-full border border-red-200 bg-red-50 text-red-800">
          <CardContent className="pt-6">
            <p>{error}</p>
          </CardContent>
        </Card>
      ) : article ? (
        <div className="space-y-6">
          <Card className="w-full">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Knowledgebase</span>
              </div>
              <CardTitle className="text-2xl font-bold">{article.title}</CardTitle>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                <span>Auteur: {article.author}</span>
                <span>Laatst bijgewerkt: {formatDate(article.updated_at)}</span>
              </div>
            </CardHeader>
            <CardContent>
              <div 
                className="prose max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: article.content }} 
              />
              
              {article.tags && article.tags.length > 0 && (
                <div className="mt-8 pt-4 border-t">
                  <div className="flex flex-wrap items-center gap-2">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    {article.tags.map(tag => (
                      <span 
                        key={tag}
                        className="inline-block bg-muted px-2 py-1 rounded-md text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          <div className="flex justify-between items-center">
            <Button variant="outline" size="sm" onClick={() => window.history.back()}>
              Terug
            </Button>
            <div>
              <Button variant="ghost" size="sm" className="mr-2">
                Delen
              </Button>
              <Button variant="ghost" size="sm">
                Afdrukken
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <Card className="w-full">
          <CardContent className="pt-6">
            <p>Artikel niet gevonden.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default KnowledgeArticle; 